import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import userService from '../services/userService';
import { User, UserRole, CreateUserDTO, UpdateUserDTO, Privilegios } from '../types';
import {
  UserPlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  InformationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Password Management States

  const [newPassword, setNewPassword] = useState('');

  // Definición de grupos de privilegios para mejor organización
  const privilegioGroups = {
    'Menú Principal': ['ventas', 'productos', 'costuras', 'pagos', 'usuarios', 'almacenes', 'proveedores']
  };

  // Mapeo de nombres para mostrar en la UI (Coincidiendo con Sidebar)
  const privilegeLabels: Record<string, string> = {
    ventas: 'Ventas',
    productos: 'Inventario',
    costuras: 'Costuras',
    tickets: 'Costuras', // Fallback
    pagos: 'Pagos',
    usuarios: 'Usuarios',
    almacenes: 'Almacenes',
    proveedores: 'Proveedores'
  };

  // Tooltips para explicar los privilegios
  const privilegeTooltips: Record<string, string> = {
    ventas: 'Acceso al módulo de Ventas.',
    productos: 'Acceso al módulo de Inventario.',
    costuras: 'Acceso al módulo de Costuras (Tickets).',
    pagos: 'Acceso al módulo de Pagos (Solo Admin).',
    usuarios: 'Acceso al módulo de Usuarios (Solo Admin).',
    almacenes: 'Acceso al módulo de Almacenes (Solo Admin).',
    proveedores: 'Acceso al módulo de Proveedores (Solo Admin).'
  };

  // Privilegios por defecto (todos false)
  const defaultPrivilegios: Privilegios = {
    ventas: false,
    productos: false,
    tickets: false,
    costuras: false,
    pagos: false,
    usuarios: false,
    almacenes: false,
    proveedores: false,
    categorias: false,
    calendario: false,
    reportes: false
  };

  // Helper para obtener privilegios por defecto según rol
  const getPrivilegiosPorDefecto = (rol: UserRole): Privilegios => {
    if (rol === UserRole.ADMIN) {
      return Object.keys(defaultPrivilegios).reduce((acc, key) => ({
        ...acc,
        [key]: true
      }), {} as Privilegios);
    }

    if (rol === UserRole.MODISTA) {
      return {
        ...defaultPrivilegios,
        costuras: true,
        tickets: true
      };
    }

    if (rol === UserRole.VENDEDORA || rol === UserRole.VENDEDORA_EN_LINEA) {
      return {
        ...defaultPrivilegios,
        ventas: true,      // Sidebar: Ventas
        productos: true,   // Sidebar: Inventario
        costuras: true,    // Sidebar: Costuras
        tickets: true,     // Sidebar: Costuras (Internal)
        pagos: false,      // Sidebar restrictivo (Solo Admin)
        almacenes: false,  // Sidebar restrictivo (Solo Admin)
        proveedores: false // Sidebar restrictivo (Solo Admin)
      };
    }

    // Default general
    return { ...defaultPrivilegios };
  };

  const [formData, setFormData] = useState<CreateUserDTO>({
    username: '',
    email: '', // Se generará automáticamente
    password: '',
    rol_usr: UserRole.VENDEDORA,
    nombre_completo: '',
    telefono: '',
    porcentaje_comision: 3.5, // Default 3.5%
    codigo_vendedora: '',
    privilegios: getPrivilegiosPorDefecto(UserRole.VENDEDORA) // Inicializar con defaults correctos
  });

  const [deleteVerification, setDeleteVerification] = useState<any>(null);
  const [confirmDeleteText, setConfirmDeleteText] = useState('');

  useEffect(() => {
    loadUsers();
  }, [roleFilter, statusFilter, searchTerm]);

  // Actualizar privilegios cuando cambia el rol
  useEffect(() => {
    // Solo aplicar defaults si estamos creando un usuario (usuario no seleccionado)
    // O si quieres que también aplique al editar (pero cuidado de no sobreescribir configs personalizadas accidentalmente)
    // Para simplificar: Siempre aplicamos la lógica de defaults al cambiar rol manualmente.
    setFormData(prev => ({
      ...prev,
      privilegios: getPrivilegiosPorDefecto(formData.rol_usr)
    }));
  }, [formData.rol_usr]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers({
        role: roleFilter || undefined,
        status: statusFilter || undefined,
        search: searchTerm || undefined
      });
      setUsers(data);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Convertir username a email automáticamente
      const emailFromUsername = `${formData.username}@mariaregna.com`;

      const userData: CreateUserDTO = {
        ...formData,
        email: emailFromUsername,
        // Enviar codigo_vendedora solo si aplica, si está vacío el backend lo generará (para VENDEDORA) o lo ignorará
        codigo_vendedora: (formData.rol_usr === UserRole.VENDEDORA || formData.rol_usr === UserRole.VENDEDORA_EN_LINEA) && formData.codigo_vendedora ? formData.codigo_vendedora : undefined
      };

      await userService.createUser(userData);
      setShowCreateModal(false);
      resetForm();
      loadUsers();
      alert('Usuario creado exitosamente');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error al crear usuario');
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      const updateData: UpdateUserDTO = {
        username: formData.username,
        email: formData.email,
        rol_usr: formData.rol_usr,
        nombre_completo: formData.nombre_completo,
        telefono: formData.telefono,
        porcentaje_comision: formData.porcentaje_comision,
        codigo_vendedora: (formData.rol_usr === UserRole.VENDEDORA || formData.rol_usr === UserRole.VENDEDORA_EN_LINEA) ? formData.codigo_vendedora : undefined,
        privilegios: formData.privilegios,
        password: newPassword || undefined // Only send if set
      };
      await userService.updateUser(selectedUser.id_usuario, updateData);
      setShowEditModal(false);
      setSelectedUser(null);
      resetForm();
      loadUsers();
      alert('Usuario actualizado exitosamente');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error al actualizar usuario');
    }
  };



  const handleToggleStatus = async (user: User) => {
    try {
      const newStatus = !user.estado;
      await userService.toggleUserStatus(user.id_usuario, newStatus);
      loadUsers();
      alert(`Usuario ${newStatus ? 'activado' : 'desactivado'} exitosamente`);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error al cambiar estado');
    }
  };

  const handleVerifyDelete = async (user: User) => {
    try {
      const verification = await userService.verifyDelete(user.id_usuario);
      setDeleteVerification(verification);
      setSelectedUser(user);
      setShowDeleteModal(true);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error al verificar eliminación');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser || confirmDeleteText !== 'ELIMINAR') {
      alert('Debes escribir ELIMINAR para confirmar');
      return;
    }

    try {
      await userService.deleteUser(selectedUser.id_usuario);
      setShowDeleteModal(false);
      setSelectedUser(null);
      setDeleteVerification(null);
      setConfirmDeleteText('');
      loadUsers();
      alert('Usuario eliminado exitosamente');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error al eliminar usuario');
    }
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email || '',
      password: '',
      rol_usr: user.rol_usr,
      nombre_completo: user.nombre_completo || '',
      telefono: user.telefono || '',
      porcentaje_comision: user.porcentaje_comision || 0,
      codigo_vendedora: user.codigo_vendedora || '',
      // Al editar, fusionamos defaults correpondientes al rol con lo que tenga el usuario
      privilegios: { ...getPrivilegiosPorDefecto(user.rol_usr), ...(user.privilegios || {}) }
    });
    setShowEditModal(true);
  };



  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      rol_usr: UserRole.VENDEDORA,
      nombre_completo: '',
      telefono: '',
      porcentaje_comision: 0,
      privilegios: getPrivilegiosPorDefecto(UserRole.VENDEDORA),
      codigo_vendedora: ''
    });
  };

  const handleResetPrivileges = () => {
    setFormData(prev => ({
      ...prev,
      privilegios: getPrivilegiosPorDefecto(prev.rol_usr)
    }));
  };

  const togglePrivilegio = (key: keyof Privilegios) => {
    // No permitir cambios si es MODISTA o ADMIN (roles fijos)
    // Para VENDEDORA, permitimos ajustar, aunque ya vienen preseleccionados.
    if (formData.rol_usr === UserRole.MODISTA || formData.rol_usr === UserRole.ADMIN) {
      return;
    }

    setFormData(prev => {
      const newPrivilegios = { ...prev.privilegios! };
      const newValue = !newPrivilegios[key];

      newPrivilegios[key] = newValue;

      // Si cambiamos 'costuras', afectamos también a 'tickets' para mantener consistencia
      if (key === 'costuras') {
        newPrivilegios['tickets'] = newValue;
      }

      return {
        ...prev,
        privilegios: newPrivilegios
      };
    });
  };

  const handleGenerateCode = () => {
    const code = Math.floor(10000 + Math.random() * 90000).toString();
    setFormData(prev => ({ ...prev, codigo_vendedora: code }));
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'bg-purple-100 text-purple-800';
      case UserRole.VENDEDORA:
        return 'bg-blue-100 text-blue-800';
      case UserRole.VENDEDORA_EN_LINEA:
        return 'bg-cyan-100 text-cyan-800';
      case UserRole.MODISTA:
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Layout>
      <div className="-mx-4 px-4 py-6 lg:-mx-8 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <UserPlusIcon className="w-5 h-5" />
            Crear Usuario
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* ... filters ... */}
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar usuarios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Role Filter */}
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos los roles</option>
              <option value={UserRole.ADMIN}>Administrador</option>
              <option value={UserRole.VENDEDORA}>Vendedora</option>
              <option value={UserRole.VENDEDORA_EN_LINEA}>Vendedora en línea</option>
              <option value={UserRole.MODISTA}>Modista</option>
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos los estados</option>
              <option value="true">Activos</option>
              <option value="false">Inactivos</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No se encontraron usuarios
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Usuario
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Rol
                    </th>

                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Comisión
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Código
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Actividad
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id_usuario} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <UserCircleIcon className="w-10 h-10 text-gray-400" />
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.nombre_completo || user.username}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.rol_usr)}`}>
                          {user.rol_usr}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.porcentaje_comision}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(user.rol_usr === UserRole.VENDEDORA || user.rol_usr === UserRole.VENDEDORA_EN_LINEA) && user.codigo_vendedora ? (
                          <span className="font-mono bg-gray-100 px-2 py-1 rounded border border-gray-200">
                            {user.codigo_vendedora}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.estado ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Activo
                          </span>
                        ) : (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            Inactivo
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>Ventas: {user._count?.sales || 0}</div>
                        <div>Tickets: {user._count?.tickets_tickets_assignedToIdToUsuario || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">

                          <button
                            onClick={() => openEditModal(user)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Editar"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(user)}
                            className={user.estado ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}
                            title={user.estado ? 'Desactivar' : 'Activar'}
                          >
                            {user.estado ? (
                              <XCircleIcon className="w-5 h-5" />
                            ) : (
                              <CheckCircleIcon className="w-5 h-5" />
                            )}
                          </button>
                          <button
                            onClick={() => handleVerifyDelete(user)}
                            className="text-red-600 hover:text-red-900"
                            title="Eliminar"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-xl font-bold mb-4 text-gray-900">Crear Nuevo Usuario</h2>
              <form onSubmit={handleCreateUser}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ej: mariaperez"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contraseña *
                    </label>
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      minLength={6}
                      placeholder="Mínimo 6 caracteres"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rol *
                    </label>
                    <select
                      required
                      value={formData.rol_usr}
                      onChange={(e) => setFormData({ ...formData, rol_usr: e.target.value as UserRole })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={UserRole.ADMIN}>Administrador</option>
                      <option value={UserRole.VENDEDORA}>Vendedora</option>
                      <option value={UserRole.VENDEDORA_EN_LINEA}>Vendedora en línea</option>
                      <option value={UserRole.MODISTA}>Modista</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre Completo
                    </label>
                    <input
                      type="text"
                      value={formData.nombre_completo}
                      onChange={(e) => setFormData({ ...formData, nombre_completo: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ej: María Pérez"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ej: 5512345678"
                    />
                  </div>
                  {/* Codigo Vendedora Field */}
                  {(formData.rol_usr === UserRole.VENDEDORA || formData.rol_usr === UserRole.VENDEDORA_EN_LINEA) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Código de Vendedora (5 dígitos)
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          maxLength={5}
                          value={formData.codigo_vendedora || ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                            setFormData({ ...formData, codigo_vendedora: val });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                          placeholder="Ej: 12345"
                        />
                        <button
                          type="button"
                          onClick={handleGenerateCode}
                          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-200 text-sm font-medium transition-colors"
                        >
                          Generar
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Si se deja vacío, se generará uno automáticamente.
                      </p>
                    </div>
                  )}
                </div>

                {/* Privilegios Section Redesigned */}
                <div className="mt-8 border-t pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <label className="block text-lg font-medium text-gray-900">
                      Privilegios y Permisos
                      {formData.rol_usr === UserRole.MODISTA && (
                        <span className="ml-2 text-sm font-normal text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Modista (Acceso limitado)</span>
                      )}
                      {formData.rol_usr === UserRole.ADMIN && (
                        <span className="ml-2 text-sm font-normal text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">Admin (Acceso total)</span>
                      )}
                    </label>

                    {/* Botón de reset */}
                    {(formData.rol_usr === UserRole.VENDEDORA || formData.rol_usr === UserRole.VENDEDORA_EN_LINEA) && (
                      <button
                        type="button"
                        onClick={handleResetPrivileges}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors"
                      >
                        <ArrowPathIcon className="w-4 h-4" />
                        Restablecer a valores por defecto
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Object.entries(privilegioGroups).map(([groupName, keys]) => (
                      <div key={groupName} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm col-span-2">
                        <h3 className="text-sm font-bold text-gray-800 mb-4 border-b pb-2 uppercase tracking-wide flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                          Vistas del Sidebar
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {keys.map((key) => {
                            const privilegioKey = key as keyof Privilegios;
                            const isDisabled = formData.rol_usr === UserRole.MODISTA || formData.rol_usr === UserRole.ADMIN;
                            const isChecked = formData.privilegios?.[privilegioKey] || false;

                            return (
                              <label
                                key={key}
                                className={`flex items-center justify-between p-2 rounded-lg transition-colors ${isDisabled ? 'opacity-50 cursor-not-allowed' :
                                  isChecked ? 'bg-blue-50 cursor-pointer' : 'hover:bg-white cursor-pointer'
                                  }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-700 font-medium capitalize">
                                    {privilegeLabels[key] || key}
                                  </span>
                                  <div className="group relative flex items-center">
                                    <InformationCircleIcon className="w-4 h-4 text-gray-400 hover:text-blue-500 cursor-help" />
                                    <div className="absolute left-1/2 bottom-full mb-2 -translate-x-1/2 w-48 bg-gray-900 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-center">
                                      {privilegeTooltips[key]}
                                      <div className="absolute top-100 left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>
                                </div>
                                <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => togglePrivilegio(privilegioKey)}
                                    disabled={isDisabled}
                                    className="toggle-checkbox absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer disabled:cursor-not-allowed"
                                    style={{
                                      right: isChecked ? '0' : 'auto',
                                      left: isChecked ? 'auto' : '0',
                                      borderColor: isChecked ? '#3b82f6' : '#d1d5db'
                                    }}
                                  />
                                  <label className={`toggle-label block overflow-hidden h-5 rounded-full cursor-pointer ${isChecked ? 'bg-blue-600' : 'bg-gray-300'}`}></label>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => { setShowCreateModal(false); resetForm(); }}
                    className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
                  >
                    Crear Usuario
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowEditModal(false)}>
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-xl font-bold mb-4 text-gray-900">Editar Usuario</h2>
              <form onSubmit={handleUpdateUser}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contraseña (Opcional)
                    </label>
                    <input
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      minLength={6}
                      placeholder="Dejar en blanco para mantener actual"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Escribe una nueva contraseña solo si deseas cambiarla.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rol *
                    </label>
                    <select
                      required
                      value={formData.rol_usr}
                      onChange={(e) => setFormData({ ...formData, rol_usr: e.target.value as UserRole })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={UserRole.ADMIN}>Administrador</option>
                      <option value={UserRole.VENDEDORA}>Vendedora</option>
                      <option value={UserRole.VENDEDORA_EN_LINEA}>Vendedora en línea</option>
                      <option value={UserRole.MODISTA}>Modista</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre Completo
                    </label>
                    <input
                      type="text"
                      value={formData.nombre_completo}
                      onChange={(e) => setFormData({ ...formData, nombre_completo: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Porcentaje de Comisión (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.porcentaje_comision}
                      onChange={(e) => setFormData({ ...formData, porcentaje_comision: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Se configura según el desempeño del usuario
                    </p>
                  </div>

                  {/* Codigo Vendedora Field (Edit) */}
                  {(formData.rol_usr === UserRole.VENDEDORA || formData.rol_usr === UserRole.VENDEDORA_EN_LINEA) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Código de Vendedora (5 dígitos)
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          maxLength={5}
                          value={formData.codigo_vendedora || ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                            setFormData({ ...formData, codigo_vendedora: val });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                          placeholder="Código único"
                        />
                        <button
                          type="button"
                          onClick={handleGenerateCode}
                          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-200 text-sm font-medium transition-colors"
                        >
                          Generar
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Privilegios Section (Added for Edit Modal) */}
                <div className="mt-8 border-t pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <label className="block text-lg font-medium text-gray-900">
                      Privilegios y Permisos
                      {formData.rol_usr === UserRole.MODISTA && (
                        <span className="ml-2 text-sm font-normal text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Modista (Acceso limitado)</span>
                      )}
                      {formData.rol_usr === UserRole.ADMIN && (
                        <span className="ml-2 text-sm font-normal text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">Admin (Acceso total)</span>
                      )}
                    </label>
                  </div>

                  {/* Botón de reset para Edit Modal también, si se desea */}
                  <div className="flex justify-end mb-2">
                    {(formData.rol_usr === UserRole.VENDEDORA || formData.rol_usr === UserRole.VENDEDORA_EN_LINEA) && (
                      <button
                        type="button"
                        onClick={handleResetPrivileges}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors"
                      >
                        <ArrowPathIcon className="w-4 h-4" />
                        Restablecer a valores por defecto
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {Object.entries(privilegioGroups).map(([groupName, keys]) => (
                      <div key={groupName} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm col-span-2">
                        <h3 className="text-sm font-bold text-gray-800 mb-4 border-b pb-2 uppercase tracking-wide flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                          Vistas del Sidebar
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {keys.map((key) => {
                            const privilegioKey = key as keyof Privilegios;
                            const isDisabled = formData.rol_usr === UserRole.MODISTA || formData.rol_usr === UserRole.ADMIN;
                            const isChecked = formData.privilegios?.[privilegioKey] || false;

                            return (
                              <label
                                key={key}
                                className={`flex items-center justify-between p-2 rounded-lg transition-colors ${isDisabled ? 'opacity-50 cursor-not-allowed' :
                                  isChecked ? 'bg-blue-50 cursor-pointer' : 'hover:bg-white cursor-pointer'
                                  }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-700 font-medium capitalize">
                                    {privilegeLabels[key] || key}
                                  </span>
                                  <div className="group relative flex items-center">
                                    <InformationCircleIcon className="w-4 h-4 text-gray-400 hover:text-blue-500 cursor-help" />
                                    <div className="absolute left-1/2 bottom-full mb-2 -translate-x-1/2 w-48 bg-gray-900 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-center">
                                      {privilegeTooltips[key]}
                                      <div className="absolute top-100 left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>
                                </div>
                                <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => togglePrivilegio(privilegioKey)}
                                    disabled={isDisabled}
                                    className="toggle-checkbox absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer disabled:cursor-not-allowed"
                                    style={{
                                      right: isChecked ? '0' : 'auto',
                                      left: isChecked ? 'auto' : '0',
                                      borderColor: isChecked ? '#3b82f6' : '#d1d5db'
                                    }}
                                  />
                                  <label className={`toggle-label block overflow-hidden h-5 rounded-full cursor-pointer ${isChecked ? 'bg-blue-600' : 'bg-gray-300'}`}></label>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => { setShowEditModal(false); setSelectedUser(null); resetForm(); }}
                    className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
                  >
                    Guardar Cambios
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}



        {/* Delete Modal */}
        {showDeleteModal && selectedUser && deleteVerification && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
              {/* Header */}
              <div className="bg-red-600 px-6 py-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Eliminar Usuario
                </h2>
              </div>

              {/* Body */}
              <div className="p-6">
                <p className="text-gray-800 mb-4 text-center text-lg">
                  ¿Estás seguro de eliminar al usuario <span className="font-bold text-gray-900">{selectedUser.nombre_completo || selectedUser.username}</span>?
                </p>

                {deleteVerification.hasRelations && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 rounded">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="text-sm font-semibold text-yellow-800 mb-2">
                          Este usuario tiene registros asociados:
                        </p>
                        <ul className="text-sm text-yellow-700 space-y-1">
                          {deleteVerification.relationCounts.sales > 0 && (
                            <li className="flex items-center">
                              <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                              {deleteVerification.relationCounts.sales} ventas
                            </li>
                          )}
                          {deleteVerification.relationCounts.tickets_tickets_assignedToIdToUsuario > 0 && (
                            <li className="flex items-center">
                              <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                              {deleteVerification.relationCounts.tickets_tickets_assignedToIdToUsuario} tickets asignados
                            </li>
                          )}
                          {deleteVerification.relationCounts.comisiones > 0 && (
                            <li className="flex items-center">
                              <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                              {deleteVerification.relationCounts.comisiones} comisiones
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-700 mb-3 text-center">
                    Esta acción <span className="font-semibold text-red-600">no se puede deshacer</span>. Para confirmar, escribe <span className="font-bold bg-gray-200 px-2 py-1 rounded">ELIMINAR</span> a continuación:
                  </p>
                  <input
                    type="text"
                    value={confirmDeleteText}
                    onChange={(e) => setConfirmDeleteText(e.target.value)}
                    placeholder="Escribe ELIMINAR"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all text-center font-medium"
                    autoFocus
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedUser(null);
                    setDeleteVerification(null);
                    setConfirmDeleteText('');
                  }}
                  className="px-5 py-2.5 border-2 border-gray-300 rounded-lg bg-white text-gray-700 font-medium hover:bg-gray-100 hover:border-gray-400 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={confirmDeleteText !== 'ELIMINAR'}
                  className="px-5 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:hover:bg-gray-300 transition-all shadow-sm"
                >
                  Eliminar Usuario
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UsersPage;